import { path } from 'ramda';
import { delay } from 'redux-saga';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import contactService from 'services/contactService';

import history from 'utils/browserHistory';

import { entitiesStore } from '../entities/actions';
import {
  accountDetails,
  addContact,
  deleteAccount,
  deleteBillingAddress,
  deleteFacility,
  fetchAccount,
  fetchBillingAddress,
  fetchContact,
  fetchFacility,
  removeContact,
  types,
  updateMetadata,
} from './actions';
import { getAccountId } from './reducer';

export default function* root() {
  yield takeLatest(types.ACCOUNT_DETAILS_INIT, watchFetchAccount);
  yield takeLatest(types.ADD_CONTACT_REQUEST, watchAddContact);
  yield takeLatest(types.DELETE_ACCOUNT_REQUEST, watchDeleteAccount);
  yield takeEvery(
    types.DELETE_BILLING_ADDRESS_REQUEST,
    watchDeleteBillingAddress,
  );
  yield takeEvery(types.DELETE_FACILITY_REQUEST, watchDeleteFacility);
  yield takeLatest(types.FETCH_ACCOUNT_REQUEST, watchFetchAccount);
  yield takeLatest(
    types.FETCH_BILLING_ADDRESS_REQUEST,
    watchFetchBillingAddress,
  );
  yield takeLatest(types.FETCH_CONTACT_REQUEST, watchFetchContact);
  yield takeLatest(types.FETCH_FACILITY_REQUEST, watchFetchFacility);
  yield takeEvery(types.REMOVE_CONTACT_REQUEST, watchRemoveContact);
  yield takeLatest(types.UPDATE_METADATA_REQUEST, watchUpdateMetadata);
}

export function* initAccountDetailsPage({ match }) {
  const accountId = path(['params', 'accountId'], match);
  yield put(accountDetails.init({ accountId }));
}

function* watchDeleteAccount() {
  try {
    const accountId = yield select(getAccountId);
    yield call(contactService.accounts.delete, accountId);
    // hack to let us re-index es
    yield delay(1750);
    yield put(deleteAccount.success());
    history.goBack();
  } catch (error) {
    yield put(deleteAccount.error(error));
  }
}

function* watchDeleteBillingAddress(action) {
  try {
    const { billingAddressId } = action.payload;
    yield call(
      contactService.accounts.billingAddresses.delete,
      billingAddressId,
    );
    yield put(deleteBillingAddress.success({ billingAddressId }));
  } catch (error) {
    yield put(deleteBillingAddress.error(error));
  }
}

function* watchDeleteFacility(action) {
  try {
    const { facilityId } = action.payload;
    yield call(contactService.accounts.facilities.delete, facilityId);
    yield put(deleteFacility.success({ facilityId }));
  } catch (error) {
    yield put(deleteFacility.error(error));
  }
}

function* watchFetchAccount() {
  try {
    const accountId = yield select(getAccountId);
    const response = yield call(contactService.accounts.get, accountId);
    yield put(entitiesStore.receiveAccount(response));
    yield put(fetchAccount.success());
  } catch (error) {
    yield put(fetchAccount.error(error));
  }
}

function* watchFetchBillingAddress(action) {
  try {
    const { billingAddressId } = action.payload;
    const response = yield call(
      contactService.accounts.billingAddresses.get,
      billingAddressId,
    );

    yield put(entitiesStore.receiveBillingAddress(response));
    yield put(fetchBillingAddress.success({ billingAddressId }));
  } catch (error) {
    yield put(fetchBillingAddress.error(error));
  }
}

function* watchFetchContact(action) {
  try {
    const { contactId } = action.payload;
    const response = yield call(contactService.contacts.get, contactId);

    yield put(entitiesStore.receiveContact(response));
    yield put(fetchContact.success({ contactId }));
  } catch (error) {
    yield put(fetchContact.error(error));
  }
}

function* watchFetchFacility(action) {
  try {
    const { facilityId } = action.payload;
    const response = yield call(
      contactService.accounts.facilities.get,
      facilityId,
    );

    yield put(entitiesStore.receiveFacility(response));
    yield put(fetchFacility.success({ facilityId }));
  } catch (error) {
    yield put(fetchFacility.error(error));
  }
}

function* watchAddContact(action) {
  try {
    const accountId = yield select(getAccountId);
    const { contactId } = action.payload;
    const response = yield call(contactService.contacts.update, contactId, {
      accountId,
    });

    yield put(entitiesStore.receiveContact(response));
    yield put(addContact.success({ contactId }));
  } catch (error) {
    yield put(addContact.error(error));
  }
}

function* watchRemoveContact(action) {
  try {
    const { contactId } = action.payload;
    const response = yield call(contactService.contacts.update, contactId, {
      accountId: null,
    });

    yield put(entitiesStore.receiveContact(response));
    yield put(removeContact.success({ contactId }));
  } catch (error) {
    yield put(removeContact.error(error));
  }
}

function* watchUpdateMetadata(action) {
  try {
    const accountId = yield select(getAccountId);
    const { metadata } = action.payload;
    const response = yield call(contactService.accounts.update, accountId, {
      metadata,
    });
    yield put(entitiesStore.receiveAccount(response));
    yield put(updateMetadata.success());
  } catch (error) {
    yield put(updateMetadata.error(error));
  }
}
